import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as itinerariesApi from './itineraries.api';
import * as tboApi from './tbo.api';

import type {
  CreateItineraryComponentRequest,
  CreateItineraryDayRequest,
  CreateItineraryFromLeadRequest,
  ItineraryApprovalActionRequest,
  ItineraryAutoSearchPreviewRequest,
  ItineraryListQuery,
  PublishItineraryRequest,
  TboFlightFareQuoteInput,
  TboFlightFareRuleInput,
  TboFlightFareUpsellInput,
  TboFlightSearchInput,
  TboHotelLiveSearchInput,
  TboHotelRoomsInput,
  UpdateItineraryComponentRequest,
  UpdateItineraryDayRequest,
  UpdateItineraryRequest,
  SaveItineraryPlanRequest,
  LegRoomsOverride,
  RepriceResponse,
  FdRepriceRequest,
} from '@utazzo/shared';

import * as citiesApi from '@/features/cities/cities.api';
import { extractServerError } from '@/lib/api-error';

/**
 * React Query hooks for the itinerary admin surface (B5.A onwards).
 * Mirrors the transfers/hotels list+detail pattern so the DataGrid + edit page
 * wiring stays consistent across modules.
 */

const QK = {
  list: (query: ItineraryListQuery) => ['itineraries', 'list', query] as const,
  detail: (id: string) => ['itineraries', 'detail', id] as const,
  markupSuggestion: (id: string) => ['itineraries', id, 'markup-suggestion'] as const,
  flightPlan: (id: string) => ['itineraries', id, 'flight-plan'] as const,
  days: (id: string) => ['itineraries', id, 'days'] as const,
  components: (id: string) => ['itineraries', id, 'components'] as const,
  approvals: (id: string) => ['itineraries', id, 'approvals'] as const,
  revisions: (id: string) => ['itineraries', id, 'revisions'] as const,
  versionDiff: (id: string) => ['itineraries', id, 'diff-from-parent'] as const,
  pricingLogs: (id: string) => ['itineraries', id, 'pricing-logs'] as const,
  paymentPlan: (id: string) => ['itineraries', id, 'payment-plan'] as const,
};

export const useItinerariesList = (query: ItineraryListQuery, opts?: { enabled?: boolean }) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => itinerariesApi.listItineraries(query),
    staleTime: 30_000,
    // Callers that only sometimes need the list (e.g. the WhatsApp widget's
    // version picker, which waits for a lead id) gate the fetch here.
    enabled: opts?.enabled ?? true,
  });

export const useItineraryDetail = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => itinerariesApi.getItinerary(id as string),
    staleTime: 30_000,
    enabled: Boolean(id),
  });

/** Auto-computed markup suggestion (quote engine) for PricingStep pre-fill. */
export const useMarkupSuggestion = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.markupSuggestion(id ?? ''),
    queryFn: () => itinerariesApi.getMarkupSuggestion(id as string),
    staleTime: 30_000,
    enabled: Boolean(id),
  });

/** Server-authoritative flight journey shape (round-trip bundle vs one-way),
 *  derived from the configured legs. Drives how the flight picker opens. */
export const useItineraryFlightPlan = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.flightPlan(id ?? ''),
    queryFn: () => itinerariesApi.getItineraryFlightPlan(id as string),
    staleTime: 30_000,
    enabled: Boolean(id),
  });

/** Apply markup: persists per-line component markup, then refreshes components. */
export const useApplyMarkup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => itinerariesApi.applyMarkup(id),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: QK.components(id) });
      qc.invalidateQueries({ queryKey: QK.detail(id) });
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to apply markup'),
  });
};

export const useUpdateItinerary = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateItineraryRequest }) =>
      itinerariesApi.updateItinerary(id, payload),
    onSuccess: (itinerary) => {
      qc.invalidateQueries({ queryKey: ['itineraries', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(itinerary.id) });
      toast.success('Itinerary updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update itinerary'),
  });
};

/** Save Step-1 plan (legs + rooms) and re-sync days, server-side. */
export const useSaveItineraryPlan = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveItineraryPlanRequest) =>
      itinerariesApi.saveItineraryPlan(itineraryId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.days(itineraryId) });
      qc.invalidateQueries({ queryKey: ['itineraries', 'list'] });
      toast.success('Plan saved');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to save the plan'),
  });
};

/** Set or clear a stay's (city leg's) room-config override. */
export const useSetLegRoomsOverride = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ legId, rooms }: { legId: string; rooms: LegRoomsOverride | null }) =>
      itinerariesApi.setLegRoomsOverride(itineraryId, legId, rooms),
    onSuccess: (_data, { rooms }) => {
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.days(itineraryId) });
      toast.success(rooms ? 'Stay rooms updated' : 'Stay rooms reset to global');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update stay rooms'),
  });
};

/** Create a fresh version on the revision chain (blank or deep copy-forward). */
export const useCreateItineraryVersion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, copyForward }: { id: string; copyForward: boolean }) =>
      itinerariesApi.createItineraryVersion(id, copyForward),
    onSuccess: (itinerary) => {
      // Seed the new detail cache immediately so Configure / Pricing open with
      // copied visa / insurance / discount ids (don't wait for a refetch that
      // can briefly render empty pickers).
      qc.setQueryData(QK.detail(itinerary.id), itinerary);
      qc.invalidateQueries({ queryKey: ['itineraries', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(itinerary.id) });
      // Refresh the lead-wide version dropdown so the newly-created draft
      // appears without a page reload.
      qc.invalidateQueries({ queryKey: ['itineraries', 'versions'] });
      toast.success('New version created');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create a new version'),
  });
};

/**
 * Revise = mint a NEW version row with the current data copied forward.
 * The server delegates to createNewVersion({copyForward:true}) so the response
 * is a fresh Itinerary (not the original). Invalidate everything version-
 * related so the dropdown + list refresh instantly; the caller is expected
 * to navigate to the new itinerary id from the mutation success.
 */
export const useReviseItinerary = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => itinerariesApi.reviseItinerary(id),
    onSuccess: (next) => {
      qc.setQueryData(QK.detail(next.id), next);
      qc.invalidateQueries({ queryKey: QK.detail(id) });
      qc.invalidateQueries({ queryKey: QK.detail(next.id) });
      qc.invalidateQueries({ queryKey: ['itineraries', 'list'] });
      qc.invalidateQueries({ queryKey: ['itineraries', 'versions'] });
      toast.success(`Version ${next.revisionNumber} opened for editing`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Could not re-open'),
  });
};

/** All published sub-versions for a lead's itineraries (version-history + Won picker). */
export const useItineraryVersions = (leadId: string | null | undefined) =>
  useQuery({
    queryKey: ['itineraries', 'versions', leadId],
    queryFn: () => itinerariesApi.listItineraryVersions(leadId as string),
    enabled: Boolean(leadId),
    staleTime: 15_000,
  });

/** Field-level diff vs parent version (Changes tab). */
export const useItineraryVersionDiff = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.versionDiff(id ?? ''),
    queryFn: () => itinerariesApi.getItineraryVersionDiff(id as string),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

/** Pricing audit log for the admin Logs tab. Auto-refreshes every 30s so a
 *  pending customer reprice shows up without a manual refresh. */
export const useItineraryPricingLogs = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.pricingLogs(id ?? ''),
    queryFn: () => itinerariesApi.getItineraryPricingLogs(id as string),
    enabled: Boolean(id),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

/**
 * Reprice (Task 4): back-solve the discount/upsell to hit an exact target
 * grand total. No hook-level onError toast on purpose - a below-floor
 * target or an FD itinerary comes back as a 400 that PricingStep shows
 * inline next to the reprice input (via `mutation.error`), not as a toast.
 */
export const useRepriceItinerary = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (targetPaise: string): Promise<RepriceResponse> =>
      itinerariesApi.repriceItinerary(itineraryId, { targetPaise }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
    },
  });
};

/**
 * Fixed Departure removal-reprice: drop the visa / insurance supplement and/or
 * apply an other-discount. Like {@link useRepriceItinerary}, no hook-level error
 * toast - PricingStep surfaces a 400 inline. A success toast confirms the apply.
 */
export const useFdReprice = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: FdRepriceRequest): Promise<RepriceResponse> =>
      itinerariesApi.repriceFdItinerary(itineraryId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
    },
  });
};

/** Clear an active reprice - unlocks the discount/upsell dropdowns again. */
export const useClearReprice = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => itinerariesApi.clearItineraryReprice(itineraryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
      toast.success('Reprice cleared');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to clear reprice'),
  });
};

/**
 * Targeted component patch — used by the long-haul inline net-price editor
 * on the Days tab. Bypasses the debounced draft/autosave path so the save
 * button's click commits immediately; invalidates components + detail so
 * the recomputed `netCostPaise` cascades back to the UI.
 */
export const useUpdateItineraryComponentPatch = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      componentId,
      payload,
    }: {
      componentId: string;
      payload: UpdateItineraryComponentRequest;
    }) => itinerariesApi.updateItineraryComponent(itineraryId, componentId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.components(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Could not save the net price'),
  });
};

/**
 * Persisted PaymentPlan for the Pricing tab. Auto-refreshes every 30s so
 * a customer's I1 payment (webhook → LOCKED) shows up without manual
 * refresh. Returns `null` when the itinerary has no plan yet.
 */
export const useItineraryPaymentPlan = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.paymentPlan(id ?? ''),
    queryFn: () => itinerariesApi.getItineraryPaymentPlan(id as string),
    enabled: Boolean(id),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

/** Auto-search cheapest hotels/flights for the plan (Step-2 pre-fill). */
export const useAutoSearchItinerary = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => itinerariesApi.autoSearchItinerary(itineraryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.days(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.components(itineraryId) });
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Auto-search could not complete; add items manually.'),
  });
};

/** Resync: fetch fresh cheapest flights/hotels WITHOUT persisting (staged in the draft). */
export const useAutoSearchPreview = (itineraryId: string) =>
  useMutation({
    mutationFn: (body: ItineraryAutoSearchPreviewRequest) =>
      itinerariesApi.previewAutoSearch(itineraryId, body),
  });

/** Reconcile the day-by-day after the trip shape changed - returns the add/remove
 *  delta WITHOUT persisting (staged into the draft, then autosaved). Silent on
 *  error: a reconcile is best-effort and must never block the editor. */
export const useReconcileItinerary = (itineraryId: string) =>
  useMutation({
    mutationFn: () => itinerariesApi.reconcileAutoSearch(itineraryId),
  });

/** Flights-only reconcile for a fixed departure in LIVE mode - fetches fares for
 *  the configured legs without disturbing the template hotels / land package. */
export const useReconcileItineraryFlights = (itineraryId: string) =>
  useMutation({
    mutationFn: () => itinerariesApi.reconcileAutoSearchFlights(itineraryId),
  });

export const useDeleteItinerary = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => itinerariesApi.deleteItinerary(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['itineraries', 'list'] });
      toast.success('Itinerary deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete itinerary'),
  });
};

export const useSetItineraryActive = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      itinerariesApi.setItineraryActive(id, active),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['itineraries', 'list'] });
      qc.invalidateQueries({ queryKey: ['itineraries', 'detail'] });
      toast.success(vars.active ? 'Itinerary activated' : 'Itinerary deactivated');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to change activation state'),
  });
};

// --- Days (B5.D) ---------------------------------------------------------

export const useItineraryDays = (itineraryId: string | null | undefined) =>
  useQuery({
    queryKey: QK.days(itineraryId ?? ''),
    queryFn: () => itinerariesApi.listItineraryDays(itineraryId as string),
    staleTime: 15_000,
    enabled: Boolean(itineraryId),
  });

export const useItineraryComponents = (itineraryId: string | null | undefined) =>
  useQuery({
    queryKey: QK.components(itineraryId ?? ''),
    queryFn: () => itinerariesApi.listItineraryComponents(itineraryId as string),
    staleTime: 15_000,
    enabled: Boolean(itineraryId),
  });

export const useAddItineraryDay = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateItineraryDayRequest) =>
      itinerariesApi.createItineraryDay(itineraryId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.days(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
      toast.success('Day added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add day'),
  });
};

export const useUpdateItineraryDay = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dayId, payload }: { dayId: string; payload: UpdateItineraryDayRequest }) =>
      itinerariesApi.updateItineraryDay(itineraryId, dayId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.days(itineraryId) });
      toast.success('Day updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update day'),
  });
};

const addDaysIso = (iso: string, n: number): string => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Cheapest refundable option, else cheapest overall. */
const preferredHotel = (
  options: { hotelCode: string; pricePaise: string; isRefundable: boolean }[],
): (typeof options)[number] | null => {
  if (options.length === 0) return null;
  const byPrice = [...options].sort((a, b) => Number(a.pricePaise) - Number(b.pricePaise));
  return byPrice.find((o) => o.isRefundable) ?? byPrice[0] ?? null;
};

export interface GeneratePlanDestination {
  cityId: string;
  nights: number;
}
export interface GeneratePlanRoom {
  adults: number;
  children: number;
}

/**
 * Generate-plan transaction (stepper step 1), mirroring the legacy
 * GetItineraryPackage: save the master config, create the day rows in order,
 * then for each destination resolve its TBO city and live-search hotels,
 * auto-attaching a preferred hotel to the first day of the block. Auto-search
 * is best-effort - a city with no TBO mapping / no results / TBO creds missing
 * is skipped without failing the generate. Returns a summary for the toast.
 */
export const useGenerateItineraryPlan = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      master,
      startDate,
      destinations,
      rooms,
    }: {
      master: UpdateItineraryRequest;
      startDate: string;
      destinations: GeneratePlanDestination[];
      rooms: GeneratePlanRoom[];
    }): Promise<{ days: number; hotelsAdded: number; citiesSkipped: number }> => {
      await itinerariesApi.updateItinerary(itineraryId, master);

      const paxRooms = rooms.map((r) => ({
        adults: r.adults,
        children: r.children,
        childAges: r.children > 0 ? Array.from({ length: r.children }, () => 10) : [],
      }));
      let dayIndex = 1;
      let offset = 0;
      let hotelsAdded = 0;
      let citiesSkipped = 0;

      for (const dest of destinations) {
        const checkIn = addDaysIso(startDate, offset);
        let firstDayId: string | null = null;
        for (let n = 0; n < dest.nights; n += 1) {
          const day = await itinerariesApi.createItineraryDay(itineraryId, {
            dayIndex,
            date: addDaysIso(startDate, offset),
            destinationCityId: dest.cityId,
          });
          if (firstDayId === null) firstDayId = day.id;
          dayIndex += 1;
          offset += 1;
        }
        const checkOut = addDaysIso(startDate, offset);

        // Best-effort live hotel auto-search + preferred pre-select.
        try {
          const city = await citiesApi.getCity(dest.cityId);
          if (!city.tboCityId) {
            citiesSkipped += 1;
            continue;
          }
          const res = await tboApi.searchTboHotels({
            currencyCode: 'INR',
            tboCityId: city.tboCityId,
            additionalTboCityIds: city.additionalTboCityIds ?? [],
            checkIn,
            checkOut,
            paxRooms,
            guestNationality: 'IN',
          });
          const pick = preferredHotel(res.options);
          if (!pick || !firstDayId) {
            citiesSkipped += 1;
            continue;
          }
          const full = res.options.find((o) => o.hotelCode === pick.hotelCode)!;
          await itinerariesApi.createItineraryComponent(itineraryId, firstDayId, {
            kind: 'HOTEL',
            source: 'TBO',
            title: `${full.name}${full.starRating ? ` · ${full.starRating}*` : ''}`,
            tboHotelCode: full.hotelCode,
            costPaise: full.pricePaise,
            isRefundable: full.isRefundable,
            startDate: checkIn,
            endDate: checkOut,
            metaJson: {
              provider: 'TBO',
              autoSelected: true,
              resultIndex: full.resultIndex,
              traceId: res.traceId ?? null,
              currencyCode: full.currencyCode,
              starRating: full.starRating,
            },
          });
          hotelsAdded += 1;
        } catch {
          // No TBO mapping / no creds / no results - leave the city for a manual pick.
          citiesSkipped += 1;
        }
      }

      return { days: dayIndex - 1, hotelsAdded, citiesSkipped };
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: QK.days(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.components(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
      qc.invalidateQueries({ queryKey: ['itineraries', 'list'] });
      const tail =
        res.hotelsAdded > 0
          ? ` · ${res.hotelsAdded} hotel${res.hotelsAdded === 1 ? '' : 's'} auto-added`
          : '';
      const skip =
        res.citiesSkipped > 0 ? ` · ${res.citiesSkipped} city(ies) need a manual hotel` : '';
      toast.success(`Plan generated (${res.days} days)${tail}${skip}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to generate the plan'),
  });
};

export const useDeleteItineraryDay = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dayId: string) => itinerariesApi.deleteItineraryDay(itineraryId, dayId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.days(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
      toast.success('Day removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove day'),
  });
};

// --- Create from lead (the build entry point) ----------------------------

export const useCreateItineraryFromLead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      leadId,
      payload,
    }: {
      leadId: string;
      payload: CreateItineraryFromLeadRequest;
    }) => itinerariesApi.createItineraryFromLead(leadId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['itineraries', 'list'] });
      toast.success('Itinerary created from lead');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to create itinerary from lead'),
  });
};

// --- Live TBO search (hotels + flights) -----------------------------------

export const useTboHotelSearch = () =>
  useMutation({
    mutationFn: (input: TboHotelLiveSearchInput) => tboApi.searchTboHotels(input),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Hotel search failed'),
  });

/** One-way, round-trip, or multi-city - the journey shape is set by how many
 *  segments the caller passes. */
export const useTboFlightSearch = () =>
  useMutation({
    mutationFn: (input: TboFlightSearchInput) => tboApi.searchTboFlights(input),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Flight search failed'),
  });

/** Live room-level options for one hotel; enabled once hotelCode + dates exist. */
export const useTboHotelRooms = (input: TboHotelRoomsInput | null) =>
  useQuery({
    queryKey: ['tbo-hotel-rooms', input],
    queryFn: () => tboApi.fetchTboHotelRooms(input as TboHotelRoomsInput),
    enabled: Boolean(input && input.hotelCode && input.checkIn && input.checkOut),
    staleTime: 60_000,
  });

/** Live hotel content (photos, address, amenities) for the rich hotel card. */
export const useTboHotelLiveDetails = (hotelCode: string | null | undefined) =>
  useQuery({
    queryKey: ['tbo-hotel-live-details', hotelCode],
    queryFn: () => tboApi.fetchTboHotelLiveDetails(hotelCode as string),
    enabled: Boolean(hotelCode),
    staleTime: 10 * 60_000,
    retry: false,
  });

export const useTboFlightFareQuote = () =>
  useMutation({
    mutationFn: (input: TboFlightFareQuoteInput) => tboApi.quoteTboFlight(input),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Fare quote failed'),
  });

export const useTboFlightFareUpsell = () =>
  useMutation({
    mutationFn: (input: TboFlightFareUpsellInput) => tboApi.upsellTboFlight(input),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Fare upsell failed'),
  });

export const useTboFlightFareRule = () =>
  useMutation({
    mutationFn: (input: TboFlightFareRuleInput) => tboApi.ruleTboFlight(input),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Fare rules failed'),
  });

export const useTboCityOptions = (countryCode: string, q: string) =>
  useQuery({
    queryKey: ['tbo', 'city-options', countryCode, q] as const,
    queryFn: () => tboApi.tboCityOptions(countryCode, q || undefined),
    enabled: countryCode.trim().length >= 2,
    staleTime: 5 * 60_000,
  });

// --- Components (B5.E) ---------------------------------------------------

/**
 * Persist a whole component diff (the day-by-day draft -> server delta) in ONE
 * batch: deletes + adds + updates fire in parallel, then the affected queries
 * are invalidated exactly once. This is what the editor's Save + autosave use
 * so a multi-component save is one round of work + one refetch, not N
 * sequential calls each invalidating + toasting. No toast here - the caller
 * decides (manual save toasts, autosave stays silent).
 */
export const useSaveItineraryComponentsDiff = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (diff: {
      deletes: string[];
      adds: { tmpId: string; dayId: string | null; payload: CreateItineraryComponentRequest }[];
      updates: { componentId: string; payload: UpdateItineraryComponentRequest }[];
    }): Promise<{ createdIds: Record<string, string> }> => {
      // Run SEQUENTIALLY, not concurrently. Each per-component mutation opens a
      // transaction that recomputes the itinerary totals and updates the same
      // itinerary row; firing them in parallel makes those transactions block on
      // that row lock and queue, and a batch of them (e.g. a re-price patching
      // several hotels + flights at once) blows the 5s interactive-transaction
      // timeout. Serial = one short transaction at a time, no contention.
      const createdIds: Record<string, string> = {};
      for (const id of diff.deletes) {
        await itinerariesApi.deleteItineraryComponent(itineraryId, id);
      }
      for (const u of diff.updates) {
        await itinerariesApi.updateItineraryComponent(itineraryId, u.componentId, u.payload);
      }
      for (const a of diff.adds) {
        const { id } = await itinerariesApi.createItineraryComponent(
          itineraryId,
          a.dayId,
          a.payload,
        );
        createdIds[a.tmpId] = id;
      }
      return { createdIds };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.components(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.days(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
    },
  });
};

// --- Approvals (B5.F) ----------------------------------------------------

export const useItineraryApprovals = (itineraryId: string | null | undefined) =>
  useQuery({
    queryKey: QK.approvals(itineraryId ?? ''),
    queryFn: () => itinerariesApi.listItineraryApprovals(itineraryId as string),
    staleTime: 15_000,
    enabled: Boolean(itineraryId),
  });

export const useItineraryApprovalAction = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ItineraryApprovalActionRequest) =>
      itinerariesApi.actOnItineraryApproval(itineraryId, payload),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: QK.approvals(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
      const verb =
        vars.action === 'SUBMIT'
          ? 'Submitted to ops'
          : vars.action === 'APPROVE'
            ? 'Approved'
            : vars.action === 'REJECT'
              ? 'Rejected'
              : 'Reverted to agent';
      toast.success(verb);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Approval action failed'),
  });
};

// --- Revisions / publish (B5.H) ------------------------------------------

export const useItineraryRevisions = (itineraryId: string | null | undefined) =>
  useQuery({
    queryKey: QK.revisions(itineraryId ?? ''),
    queryFn: () => itinerariesApi.listItineraryRevisions(itineraryId as string),
    staleTime: 15_000,
    enabled: Boolean(itineraryId),
  });

export const useCheckPublishPrices = (itineraryId: string) =>
  useMutation({
    mutationFn: () => itinerariesApi.checkPublishPrices(itineraryId),
  });

/**
 * Pre-publish preview link. Nothing is published, so no cache invalidation and
 * no success toast - the caller opens the returned URL in a new tab.
 */
export const useItineraryPreviewLink = (itineraryId: string) =>
  useMutation({
    mutationFn: () => itinerariesApi.createItineraryPreviewLink(itineraryId),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Could not build the preview - please try again.'),
  });

export const usePublishItinerary = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PublishItineraryRequest) =>
      itinerariesApi.publishItinerary(itineraryId, payload),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: QK.revisions(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.detail(itineraryId) });
      qc.invalidateQueries({ queryKey: QK.components(itineraryId) });
      // Refresh the lead-wide version dropdown so the newly-published version
      // appears without a page reload. Predicate form matches all leadIds.
      qc.invalidateQueries({ queryKey: ['itineraries', 'versions'] });
      toast.success(`Published v${res.publishedVersion}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Publish failed'),
  });
};

const extractMessage = (err: unknown): string | null => extractServerError(err).message;
